'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import type { NavLink } from '@/data/resolve';
import { cn } from '@/lib/cn';

/**
 * Is this the current page? Compared against locale-prefixed hrefs, so the home
 * link (`/uz`) has to match exactly — `startsWith` would light it up on every
 * page of the language.
 */
function isActive(pathname: string, href: string): boolean {
  return pathname === href || pathname.startsWith(`${href}/`);
}

/** Desktop pill navigation. Client-only because it reads the active route. */
export function NavLinks({
  items,
  label,
}: {
  items: readonly NavLink[];
  label: string;
}) {
  const pathname = usePathname();

  return (
    <nav
      aria-label={label}
      className="hidden rounded-full bg-surface p-1 nav:block"
    >
      <ul className="flex items-center">
        {items.map((item) => {
          const active = isActive(pathname, item.href);
          return (
            <li key={item.href}>
              <Link
                href={item.href}
                aria-current={active ? 'page' : undefined}
                className={cn(
                  // px-3 rather than px-3.5: six items at the wider padding put
                  // the row 54px over the container at xl, where the phone
                  // number joins it.
                  'block rounded-full px-3 py-2.5 text-[0.9375rem] font-semibold',
                  'whitespace-nowrap transition-colors duration-200',
                  active
                    ? 'bg-white text-ink shadow-[0_1px_2px_rgb(13_27_46/0.06),0_4px_12px_-6px_rgb(13_27_46/0.18)]'
                    : 'text-body hover:text-ink',
                )}
              >
                {item.label}
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
